import type { ConfigType, JsonValue } from "@enduring-systems/entitlements";
import { validateValueForType } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import { Textarea } from "@/common/ui/textarea";

export interface ConfigControlProps {
  readonly capabilityId: string;
  readonly type: ConfigType;
  readonly value: JsonValue;
  readonly onChange: (value: JsonValue) => void;
  readonly disabled?: boolean;
}

function serialize(value: JsonValue): string {
  return JSON.stringify(value, null, 2);
}

export function ConfigControl({
  capabilityId,
  type,
  value,
  onChange,
  disabled = false,
}: ConfigControlProps): JSX.Element {
  const [text, setText] = useState<string>(() => serialize(value));
  const [error, setError] = useState<string | null>(null);

  // Re-seed only on an external value change (late load / reset), not on the
  // control's own echo of a value it just emitted, so a valid edit is never
  // reverted when the parent mirrors onChange back into value.
  const emitted = useRef<JsonValue>(value);
  useEffect(() => {
    if (value !== emitted.current) {
      setText(serialize(value));
      setError(null);
    }
  }, [value]);

  function handleChange(next: string): void {
    setText(next);

    let parsed: JsonValue;
    try {
      parsed = JSON.parse(next) as JsonValue;
    } catch {
      setError("Invalid JSON");
      return;
    }

    const result = validateValueForType(type, parsed);
    if (!result.ok) {
      setError(result.reason);
      return;
    }

    setError(null);
    emitted.current = parsed;
    onChange(parsed);
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        id={`${capabilityId}-config`}
        data-testid="entitlement-control-config-input"
        className="min-h-32 font-mono text-sm"
        spellCheck={false}
        value={text}
        disabled={disabled}
        onChange={(event) => handleChange(event.target.value)}
      />
      {error ? (
        <p data-testid="entitlement-control-config-error" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
